import { Api } from './axios-config';

// ── Tipos ─────────────────────────────────────────────────────

export interface IWhatsAppConfig {
  id: number;
  businessId: number;
  instanceName: string;
  instanceApiKey: string;
  enabled: boolean;
  systemPrompt: string;
  messageTtlHours: number;
  maxDailyMessagesPerUser: number;
  cooldownSeconds: number;
  businessHoursOnly: boolean;
  optOutPhrases: string;
  phoneFilterMode: boolean;
  allowedPhoneNumbers?: string;
  blockedPhoneNumbers?: string;
  sendConfirmation: boolean;
  sendReminder: boolean;
  sendFeedback: boolean;
  reminderHoursBefore: number;
  confirmationMessage?: string;
  reminderMessage?: string;
  feedbackMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IUpdateWhatsAppConfig {
  enabled?: boolean;
  systemPrompt?: string;
  messageTtlHours?: number;
  maxDailyMessagesPerUser?: number;
  cooldownSeconds?: number;
  businessHoursOnly?: boolean;
  optOutPhrases?: string;
  phoneFilterMode?: boolean;
  allowedPhoneNumbers?: string | null;
  blockedPhoneNumbers?: string | null;
  sendConfirmation?: boolean;
  sendReminder?: boolean;
  sendFeedback?: boolean;
  reminderHoursBefore?: number;
  confirmationMessage?: string | null;
  reminderMessage?: string | null;
  feedbackMessage?: string | null;
}

export interface IBotTool {
  id: number;
  businessWhatsAppConfigId: number;
  name: string;
  description: string;
  enabled: boolean;
  parametersJson: string;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateBotTool {
  name: string;
  description: string;
  parametersJson?: string;
}

export interface IUpdateBotTool {
  enabled?: boolean;
  name?: string;
  description?: string;
  parametersJson?: string;
}

export interface IConnectResult {
  instanceName: string;
  qrCodeBase64: string | null;
  connected: boolean;
}

export interface IConnectionStatus {
  connected: boolean;
  instanceName: string | null;
}

export interface IHistoryMessage {
  id: number;
  phoneNumber: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  createdAt: string;
}

export interface IConversationSummary {
  phoneNumber: string;
  lastMessage: string;
  lastMessageAt: string;
  messageCount: number;
  messages?: IHistoryMessage[];
}

export interface IHistoryResult {
  conversations: IConversationSummary[];
}

// ── Config ────────────────────────────────────────────────────

const getConfig = async (businessId: number): Promise<IWhatsAppConfig | { enabled: false }> => {
  const { data } = await Api.get(`/api/business/${businessId}/whatsapp-config`);
  return data;
};

const updateConfig = async (businessId: number, config: IUpdateWhatsAppConfig): Promise<IWhatsAppConfig> => {
  const { data } = await Api.put(`/api/business/${businessId}/whatsapp-config`, config);
  return data;
};

// ── Tools ─────────────────────────────────────────────────────

const getTools = async (businessId: number): Promise<{ tools: IBotTool[] }> => {
  const { data } = await Api.get(`/api/business/${businessId}/whatsapp-tools`);
  return data;
};

const createTool = async (businessId: number, tool: ICreateBotTool): Promise<IBotTool> => {
  const { data } = await Api.post(`/api/business/${businessId}/whatsapp-tools`, tool);
  return data;
};

const updateTool = async (businessId: number, toolId: number, tool: IUpdateBotTool): Promise<IBotTool> => {
  const { data } = await Api.put(`/api/business/${businessId}/whatsapp-tools/${toolId}`, tool);
  return data;
};

const deleteTool = async (businessId: number, toolId: number): Promise<void> => {
  await Api.delete(`/api/business/${businessId}/whatsapp-tools/${toolId}`);
};

// ── Instance ──────────────────────────────────────────────────

const connectInstance = async (businessId: number): Promise<IConnectResult> => {
  const { data } = await Api.post(`/api/business/${businessId}/whatsapp/connect`);
  return data;
};

const getConnectionStatus = async (businessId: number): Promise<IConnectionStatus> => {
  const { data } = await Api.get(`/api/business/${businessId}/whatsapp/status`);
  return data;
};

const disconnectInstance = async (businessId: number): Promise<void> => {
  await Api.post(`/api/business/${businessId}/whatsapp/disconnect`);
};

// ── History ───────────────────────────────────────────────────

const getHistory = async (businessId: number, limit = 50): Promise<IHistoryResult> => {
  const { data } = await Api.get(`/api/business/${businessId}/whatsapp/history?limit=${limit}`);
  return data ?? { conversations: [] };
};

// ── Export ────────────────────────────────────────────────────

export const whatsAppService = {
  getConfig,
  updateConfig,
  getTools,
  createTool,
  updateTool,
  deleteTool,
  connectInstance,
  getConnectionStatus,
  disconnectInstance,
  getHistory,
};
